import get from "lodash/get";
import { API } from "lib/API";
import { RunDeviceExecResponse } from "types";

export type ExecResult = {
  /** Combined stdout + stderr of the command. */
  output: string;
  /** Process exit code. `0` when the command succeeded. */
  exitCode: number;
  /** Set when the command could not be run at all (transport or device error). */
  error?: string;
};

export interface ExecClient {
  exec(command: string, args: string[]): Promise<ExecResult>;
}

const errorMessage = (err: unknown, fallback: string): string =>
  get(err, "response.data.message") || get(err, "message") || fallback;

/**
 * RemoteExecClient runs commands via the admin-only automify exec endpoint.
 *
 * Where the command actually runs is the API's decision, not this client's: with
 * `REMOTE_EXEC_TARGET=local` a development API runs it on its own host instead of forwarding it
 * to the device. The contract is identical either way.
 */
export class RemoteExecClient implements ExecClient {
  constructor(private readonly storageID: string) {}

  async exec(command: string, args: string[]): Promise<ExecResult> {
    try {
      const response = await API.POST<unknown, RunDeviceExecResponse>(
        `/storages/${this.storageID}/remote_controls/exec`,
        { command, arguments: args },
      );
      return {
        output: response.data ?? "",
        // Devices running an older sentry omit exitCode; treat that as a success.
        exitCode: response.exitCode ?? 0,
      };
    } catch (err) {
      return {
        output: "",
        exitCode: 1,
        error: errorMessage(err, "Failed to run command on the device"),
      };
    }
  }
}
